<template>
  <div class="in-app-billing-products">
    <TrialSubscriptionSettings
      v-if="hasAnySubscriptionProduct"
      :package-name="packageName"
      @update="getInfo"
    />

    <template v-else>
      <SubscriptionPromote @create-button-click="openProductCreateModal" />
      <hr class="divider" />
    </template>

    <InsideContent
      :title="$t('APP.IAB.PRODUCTS_TITLE')"
      :hint="$t('HINTS.APPS.PAYMENT.PRODUCTS')"
    >
      <div class="in-app-billing-products__actions">
        <ElButton
          icon="el-icon-plus"
          type="primary"
          :loading="isCreatingProduct"
          @click="openProductCreateModal"
        >
          {{ $t('APP.IAB.NEW_PRODUCT_BTN') }}
        </ElButton>

        <div class="in-app-billing-products__filters shrink">
          <ElCheckbox v-model="onlyActives" class="shrink">
            {{ $t('APP.IAB.PRODUCT_TYPES.ONLY_ACTIVES') }}
          </ElCheckbox>
          <ElRadioGroup v-model="typeFilter" class="show-for-medium">
            <ElRadioButton label="all">{{ $t('ALL') }}</ElRadioButton>
            <ElRadioButton label="subscription">
              {{ $t('APP.IAB.PRODUCT_TYPES.SUBSCRIPTION') }}
            </ElRadioButton>
            <ElRadioButton label="purchasable">
              {{ $t('APP.IAB.PURCHASABLE') }}
            </ElRadioButton>
          </ElRadioGroup>

          <ElSelect v-model="typeFilter" class="hide-for-medium">
            <ElOption :label="$t('ALL')" value="all" />
            <ElOption
              :label="$t('APP.IAB.PRODUCT_TYPES.SUBSCRIPTION')"
              value="subscription"
            />
            <ElOption :label="$t('APP.IAB.PURCHASABLE')" value="purchasable" />
          </ElSelect>
        </div>
      </div>

      <CardApiError
        v-if="apiError"
        :error-text="$t('APP.IAB.API_ERROR')"
        @retry="getInfo"
      />

      <InAppPurchaseProductsTable
        v-else
        v-loading="isFetching"
        :products="filteredProducts"
        @edit-product="updateProduct"
      />
    </InsideContent>
  </div>
</template>

<script>
import { format as priceFormat, label as priceLabel } from '@/utils/number';
import InsideContent from '@@/common/layout/InsideContent';
import { Products } from '@/api/api';
import packageNameWatch from '@/mixins/apps/packageNameWatch';
import packageName from '@/mixins/packageName';
import getKindLabel from '@/utils/apps/kindLabel';
import TrialSubscriptionSettings from './TrialSubscriptionSettings';
import InAppPurchaseProductsTable from '../../common/in-app-purchase/InAppPurchaseProductsTable';
import InAppPurchaseProductModal from '../../common/in-app-purchase/InAppPurchaseProductModal';
import CardApiError from '@/components/common/CardApiError';
import SubscriptionPromote from './SubscriptionPromote';

export default {
  name: 'InAppBillingProducts',

  components: {
    InsideContent,
    CardApiError,
    SubscriptionPromote,
    TrialSubscriptionSettings,
    InAppPurchaseProductsTable,
  },

  mixins: [packageNameWatch, packageName],

  data() {
    return {
      typeFilter: 'all',
      products: [],
      isFetching: true,
      isCreatingProduct: false,
      apiError: false,
      onlyActives: false,
    };
  },

  computed: {
    filteredProducts() {
      let filteredByType = this.products;

      switch (this.typeFilter) {
        case 'all': {
          filteredByType = this.products;
          break;
        }
        case 'purchasable': {
          filteredByType = this.products.filter(p => p.kind === 'P');
          break;
        }
        default: {
          filteredByType = this.products.filter(p => p.kind !== 'P');
        }
      }

      return this.onlyActives
        ? filteredByType.filter(({ state }) => state === 'A')
        : filteredByType;
    },

    hasAnySubscriptionProduct() {
      return Boolean(this.products.filter(p => p.kind !== 'P').length);
    },
  },

  created() {
    this.getInfo();
  },

  methods: {
    priceLabel,
    priceFormat,

    getInfo() {
      this.apiError = false;
      this.isFetching = true;

      return Products.get({ packageName: this.packageName })
        .then(({ body: { products = [] } }) => {
          this.products = products.reverse();
        })
        .catch(({ body: { message } = {} }) => {
          if (message) {
            this.$message.error(message);
          }

          this.apiError = true;
        })
        .finally(() => {
          this.isFetching = false;
        });
    },

    getStateLabel(state) {
      if (state === 'A') {
        return this.$t('ACTIVE');
      }

      if (state === 'D') {
        return this.$t('DISABLED');
      }

      return '';
    },

    openProductCreateModal() {
      this.isCreatingProduct = true;

      return this.$modal
        .open({
          component: InAppPurchaseProductModal,
          title: this.$t('APP.IAB.ADD_PRODUCT'),
          props: {
            confirmButtonText: this.$t('APP.IAB.ADD_PRODUCT_BTN'),
          },
          width: '40%',
        })
        .then(newProduct => {
          if (!newProduct) {
            this.isCreatingProduct = false;
            return;
          }

          this.createProduct(newProduct);
        });
    },

    createProduct(newProduct) {
      this.isCreatingProduct = true;

      return Products.save({ packageName: this.packageName }, newProduct)
        .then(({ body: { product = {} } }) => {
          this.addProductToList(product);
        })
        .catch(({ body: { message } = {} }) => {
          if (message) {
            this.$message.error(message);
          }
        })
        .finally(() => {
          this.isCreatingProduct = false;
        });
    },

    updateProduct(updatedProduct) {
      const { sku, ...productData } = updatedProduct;
      return Products.update(
        {
          sku,
          packageName: this.packageName,
        },
        productData,
      )
        .then(({ body: { product = {} } }) => {
          this.updateProductInList({ ...product, sku });
        })
        .catch(({ body: { message } = {} }) => {
          if (message) {
            this.$message.error(message);
          }
        });
    },

    addProductToList(product) {
      this.products.push(product);
    },

    updateProductInList(updatedProduct) {
      this.products = this.products.map(product => {
        if (product.sku === updatedProduct.sku) return updatedProduct;
        return product;
      });
    },

    translateKindLabel(kind) {
      if (kind === 'R') {
        return this.$t('APP.IAB.TRIAL_SUBSCRIPTION');
      }

      return this.$t(getKindLabel(kind));
    },
  },
};
</script>

<style lang="scss">
.in-app-billing-products {
  &__actions {
    display: flex;
    align-items: center;
    justify-content: space-between;

    @include breakpoint(medium down) {
      flex-direction: column;
      align-items: flex-start;
    }
  }

  &__filters {
    display: flex;
    align-items: center;
    justify-content: space-between;

    @include breakpoint(medium down) {
      margin-top: 1rem;
    }
  }
}
</style>
